import {
  categoryIndicators,
  mapRiskGroupName,
  mapRiskName,
  RiskIndicator,
  RiskIndicatorGroup,
  riskIndicatorGroupings,
} from "../lib/risk-contracts";
import { getRiskBucket } from "../lib/risk";
import { TODO } from "@revelate/types";

export type PipelineRiskSeverity = "high" | "medium" | "low";

export const pipelineRiskSeverityColors: Record<PipelineRiskSeverity, string> = {
  high: "#9F3A4A",
  medium: "#231942",
  low: "#9CA3AF",
};

export const pipelineConcentrationConfig = {
  highMaxDealCount: 10,
  highMaxDealShare: 0.2,
  targetValueShare: 0.5,
} as const;

export type StateOfSalesV2PipelineRiskDeal = TODO & {
  forecast_contribution: number;
};

export type PipelineRiskSeverityRow = {
  count: number;
  deals: StateOfSalesV2PipelineRiskDeal[];
  forecastValue: number;
  key: PipelineRiskSeverity;
  label: string;
  rawValue: number;
};

export type PipelineRiskIndicatorRow = {
  count: number;
  deals: StateOfSalesV2PipelineRiskDeal[];
  forecastValue: number;
  indicator: RiskIndicator;
  label: string;
  rawValue: number;
};

export type PipelineRiskThemeRow = {
  count: number;
  deals: StateOfSalesV2PipelineRiskDeal[];
  forecastValue: number;
  indicators: PipelineRiskIndicatorRow[];
  label: string;
  rawValue: number;
  riskGroup: RiskIndicatorGroup;
  shareOfExposedDealValue: number | null;
};

export type PipelineRiskTimingRow = {
  count: number;
  deals: StateOfSalesV2PipelineRiskDeal[];
  forecastValue: number;
  key: "overdue" | "next_30" | "days_31_60" | "later" | "not_specified";
  label: string;
  rawValue: number;
};

export type StateOfSalesV2PipelineRiskAnalysis = {
  deals: StateOfSalesV2PipelineRiskDeal[];
  exposedForecastShare: number | null;
  exposedForecastValue: number;
  exposedOpenDealValue: number;
  selectedPeriodForecast: number;
  severity: PipelineRiskSeverityRow[];
  themes: PipelineRiskThemeRow[];
  timing: PipelineRiskTimingRow[];
};

type ForecastContributionDeal = {
  forecast_contribution?: number;
  id: number;
};

function finiteNumber(value: unknown) {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
}

function pipelineAssessmentRisk(deal: StateOfSalesV2PipelineRiskDeal) {
  return finiteNumber(deal.pipeline_risk_score ?? deal.weighted_risk_score);
}

export type PipelineConcentrationSummary<T> = {
  dealCount: number;
  dealShare: number;
  deals: T[];
  representedShare: number;
  state: "balanced" | "high" | "unavailable";
  totalDealCount: number;
  totalValue: number;
};

/**
 * Finds the smallest group of largest open deals whose entered Deal value
 * reaches half of the selected-period open pipeline.
 *
 * This exact adapter replaces the coarse percentage stepping in the protected
 * shared risk helper without changing that helper or any existing metric.
 */
export function buildPipelineConcentrationSummary<
  T extends { id: number | string; value?: number | null },
>(
  inputDeals: T[],
): PipelineConcentrationSummary<T> {
  const deals = Array.from(
    new Map(
      inputDeals
        .filter(deal => finiteNumber(deal.value) > 0)
        .map(deal => [String(deal.id), deal]),
    ).values(),
  ).toSorted((a, b) => (
    finiteNumber(b.value) - finiteNumber(a.value)
    || String(a.id).localeCompare(String(b.id))
  ));
  const totalValue = deals.reduce(
    (sum, deal) => sum + finiteNumber(deal.value),
    0,
  );

  if (deals.length === 0 || totalValue <= 0) {
    return {
      dealCount: 0,
      dealShare: 0,
      deals: [],
      representedShare: 0,
      state: "unavailable",
      totalDealCount: deals.length,
      totalValue,
    };
  }

  const concentratedDeals: T[] = [];
  let concentratedValue = 0;
  const targetValue = totalValue * pipelineConcentrationConfig.targetValueShare;

  for (const deal of deals) {
    concentratedDeals.push(deal);
    concentratedValue += finiteNumber(deal.value);
    if (concentratedValue >= targetValue) break;
  }

  const dealShare = concentratedDeals.length / deals.length;
  const state = concentratedDeals.length
    <= pipelineConcentrationConfig.highMaxDealCount
    && dealShare <= pipelineConcentrationConfig.highMaxDealShare
    ? "high"
    : "balanced";

  return {
    dealCount: concentratedDeals.length,
    dealShare,
    deals: concentratedDeals,
    representedShare: concentratedValue / totalValue,
    state,
    totalDealCount: deals.length,
    totalValue,
  };
}

export function getPipelineRiskSeverity(score: number): PipelineRiskSeverity {
  if (score >= 50) return "high";
  if (score >= 25) return "medium";
  return "low";
}

export type PipelineRiskBubbleDeal = StateOfSalesV2PipelineRiskDeal & {
  urgency_bucket?: "high" | "medium" | "low";
  urgency_score?: number;
};

export type PipelineRiskKeyDeal = PipelineRiskBubbleDeal & {
  forecast_share: number | null;
};

export type PipelineRiskDealDriver = {
  indicator: RiskIndicator;
  label: string;
  riskContribution: number;
  explanation: string | null;
  recommendedAction: string | null;
};

export type PipelineRiskBubbleChartPoint = PipelineRiskBubbleDeal & {
  bubbleValue: number;
  pipelineRisk: number;
  plottedPipelineRisk: number;
  plottedStageProbability: number;
  stageProbability: number;
};

const bubbleCollisionOffsets = [
  { x: 0, y: 0 },
  { x: -2, y: 5 },
  { x: 2, y: -5 },
  { x: -4, y: -3 },
  { x: 4, y: 3 },
  { x: 0, y: 8 },
  { x: 0, y: -8 },
  { x: -6, y: 6 },
  { x: 6, y: -6 },
  { x: 6, y: 6 },
] as const;

function clampToChartDomain(value: number) {
  return Math.max(0, Math.min(100, value));
}

export function buildPipelineRiskBubbleChartPoints(
  deals: PipelineRiskBubbleDeal[],
): PipelineRiskBubbleChartPoint[] {
  const points: PipelineRiskBubbleChartPoint[] = [];

  for (const deal of deals) {
    const pipelineRisk = clampToChartDomain(
      pipelineAssessmentRisk(deal),
    );
    const stageProbability = clampToChartDomain(
      finiteNumber(
        deal.stage_probability_adjusted ?? deal.stage_probability,
      ) * 100,
    );
    const collisionIndex = points.filter(point => (
      Math.abs(point.stageProbability - stageProbability) <= 2.5
      && Math.abs(point.pipelineRisk - pipelineRisk) <= 5
    )).length;
    const offset = bubbleCollisionOffsets[
      Math.min(collisionIndex, bubbleCollisionOffsets.length - 1)
    ];

    points.push({
      ...deal,
      bubbleValue: Math.max(0, finiteNumber(deal.value)),
      pipelineRisk,
      plottedPipelineRisk: clampToChartDomain(pipelineRisk + offset.y),
      plottedStageProbability: clampToChartDomain(stageProbability + offset.x),
      stageProbability,
    });
  }

  return points;
}

export function selectPipelineRiskBubbleDeals(
  deals: StateOfSalesV2PipelineRiskDeal[],
  urgencyDeals: Array<{
    id: number | string;
    urgency_bucket?: "high" | "medium" | "low";
    urgency_score?: number;
  }>,
  limit = 10,
): PipelineRiskBubbleDeal[] {
  const urgencyById = new Map(
    urgencyDeals.map(deal => [String(deal.id), deal]),
  );
  const severityRank: Record<PipelineRiskSeverity, number> = {
    high: 0,
    medium: 1,
    low: 2,
  };
  const urgencyRank = {
    high: 0,
    medium: 1,
    low: 2,
  } as const;
  const getUrgencyRank = (value: unknown) => (
    value === "high" || value === "medium" || value === "low"
      ? urgencyRank[value]
      : urgencyRank.low
  );

  const comparePriority = (a: PipelineRiskBubbleDeal, b: PipelineRiskBubbleDeal) => (
    severityRank[getPipelineRiskSeverity(pipelineAssessmentRisk(a))]
      - severityRank[getPipelineRiskSeverity(pipelineAssessmentRisk(b))]
    || finiteNumber(b.value) - finiteNumber(a.value)
    || getUrgencyRank(a.urgency_bucket) - getUrgencyRank(b.urgency_bucket)
    || pipelineAssessmentRisk(b) - pipelineAssessmentRisk(a)
    || String(a.id).localeCompare(String(b.id))
  );
  const preparedDeals = deals
    .map((deal) => {
      const urgency = urgencyById.get(String(deal.id));
      return {
        ...deal,
        urgency_bucket: urgency?.urgency_bucket,
        urgency_score: urgency?.urgency_score,
      };
    })
    .toSorted(comparePriority);
  const boundedLimit = Math.max(0, limit);
  const selected = preparedDeals.slice(0, boundedLimit);
  if (!selected.length) return selected;

  const highestRiskDeal = preparedDeals.toSorted((a, b) => (
    pipelineAssessmentRisk(b) - pipelineAssessmentRisk(a)
    || finiteNumber(b.value) - finiteNumber(a.value)
    || String(a.id).localeCompare(String(b.id))
  ))[0];
  if (
    highestRiskDeal
    && !selected.some(deal => String(deal.id) === String(highestRiskDeal.id))
  ) {
    selected[selected.length - 1] = highestRiskDeal;
  }
  return selected.toSorted(comparePriority);
}

/**
 * Ranks the complete approved Pipeline Risk population by its existing score.
 * This selector does not recalculate or rebucket risk; it only provides the
 * record order required by chat questions that explicitly ask for the highest
 * scores regardless of severity.
 */
export function selectPipelineRiskRankedDeals(
  deals: StateOfSalesV2PipelineRiskDeal[],
): StateOfSalesV2PipelineRiskDeal[] {
  return deals.toSorted((a, b) => (
    pipelineAssessmentRisk(b) - pipelineAssessmentRisk(a)
    || finiteNumber(b.value) - finiteNumber(a.value)
    || finiteNumber(b.forecast_contribution) - finiteNumber(a.forecast_contribution)
    || String(a.id).localeCompare(String(b.id))
  ));
}

export function selectPipelineRiskKeyDeals(
  deals: StateOfSalesV2PipelineRiskDeal[],
  urgencyDeals: Array<{
    id: number | string;
    urgency_bucket?: "high" | "medium" | "low";
    urgency_score?: number;
  }>,
  totalForecast: number,
): PipelineRiskKeyDeal[] {
  const urgencyById = new Map(
    urgencyDeals.map(deal => [String(deal.id), deal]),
  );

  return deals
    .filter(
      deal => getPipelineRiskSeverity(
        pipelineAssessmentRisk(deal),
      ) === "high",
    )
    .map((deal) => {
      const urgency = urgencyById.get(String(deal.id));
      return {
        ...deal,
        forecast_share: totalForecast > 0
          ? finiteNumber(deal.forecast_contribution) / totalForecast
          : null,
        urgency_bucket: urgency?.urgency_bucket,
        urgency_score: urgency?.urgency_score,
      };
    })
    .toSorted((a, b) => (
      finiteNumber(b.value) - finiteNumber(a.value)
      || pipelineAssessmentRisk(b) - pipelineAssessmentRisk(a)
      || finiteNumber(b.forecast_contribution) - finiteNumber(a.forecast_contribution)
      || String(a.id).localeCompare(String(b.id))
    ));
}

/**
 * Exposes the already evaluated Pipeline Risk indicators for one deal.
 * This does not recalculate or rebucket risk; it preserves the same indicator
 * values and category used by the State of Sales Pipeline Risk analysis.
 */
export function getPipelineRiskDealDrivers(
  deal: StateOfSalesV2PipelineRiskDeal,
): PipelineRiskDealDriver[] {
  return categoryIndicators.pipelineRisk
    .flatMap((indicator): PipelineRiskDealDriver[] => {
      const evaluated = deal.risks?.[indicator] as {
        risk?: unknown;
        risk_explanation?: unknown;
        suggested_next_move?: unknown;
      } | undefined;
      const riskContribution = finiteNumber(evaluated?.risk);
      if (riskContribution <= 0) return [];
      return [{
        indicator,
        label: mapRiskName(indicator),
        riskContribution,
        explanation: typeof evaluated?.risk_explanation === "string"
          && evaluated.risk_explanation.trim()
          ? evaluated.risk_explanation.trim()
          : null,
        recommendedAction: typeof evaluated?.suggested_next_move === "string"
          && evaluated.suggested_next_move.trim()
          ? evaluated.suggested_next_move.trim()
          : null,
      }];
    })
    .toSorted((left, right) => (
      right.riskContribution - left.riskContribution
      || left.indicator.localeCompare(right.indicator)
    ));
}

function getIndicatorScore(deal: TODO, indicator: RiskIndicator) {
  return finiteNumber(deal.risks?.[indicator]?.risk);
}

function sumDealValues(deals: StateOfSalesV2PipelineRiskDeal[]) {
  return deals.reduce((sum, deal) => sum + finiteNumber(deal.value), 0);
}

function sumForecastValues(deals: StateOfSalesV2PipelineRiskDeal[]) {
  return deals.reduce(
    (sum, deal) => sum + finiteNumber(deal.forecast_contribution),
    0,
  );
}

function uniqueDeals(deals: StateOfSalesV2PipelineRiskDeal[]) {
  return Array.from(new Map(deals.map(deal => [deal.id, deal])).values());
}

function getExpectedCloseDate(deal: TODO) {
  const rawDate = deal.adjustedCloseDate;
  if (!rawDate) return null;
  const date = new Date(rawDate);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function buildStateOfSalesV2PipelineRiskAnalysis(
  evaluatedAtRiskDeals: TODO[],
  forecastDeals: ForecastContributionDeal[],
  selectedPeriodForecast: number,
  evaluationDate = new Date(),
): StateOfSalesV2PipelineRiskAnalysis {
  const pipelineRiskIndicators = new Set(categoryIndicators.pipelineRisk);
  const forecastContributionByDeal = new Map(
    forecastDeals.map(deal => [deal.id, finiteNumber(deal.forecast_contribution)]),
  );
  const deals = uniqueDeals(
    evaluatedAtRiskDeals
      .filter(deal => Array.from(pipelineRiskIndicators).some(
        indicator => getIndicatorScore(deal, indicator) > 0,
      ))
      .map(deal => ({
        ...deal,
        forecast_contribution: forecastContributionByDeal.get(deal.id) ?? 0,
      } as StateOfSalesV2PipelineRiskDeal)),
  );
  const exposedForecastValue = sumForecastValues(deals);
  const exposedOpenDealValue = sumDealValues(deals);

  const severityDefinitions: Array<{
    key: PipelineRiskSeverity;
    label: string;
  }> = [
    { key: "high", label: "High risk" },
    { key: "medium", label: "Medium risk" },
    { key: "low", label: "Low risk" },
  ];
  const severity = severityDefinitions.map(({ key, label }) => {
    const matchingDeals = deals.filter(
      deal => getPipelineRiskSeverity(pipelineAssessmentRisk(deal)) === key,
    );
    return {
      count: matchingDeals.length,
      deals: matchingDeals,
      forecastValue: sumForecastValues(matchingDeals),
      key,
      label,
      rawValue: sumDealValues(matchingDeals),
    };
  });

  const themes = (Object.entries(riskIndicatorGroupings) as Array<[
    RiskIndicatorGroup,
    RiskIndicator[],
  ]>).map(([riskGroup, groupedIndicators]) => {
    const indicators = groupedIndicators
      .filter(indicator => pipelineRiskIndicators.has(indicator))
      .map((indicator): PipelineRiskIndicatorRow => {
        const indicatorDeals = deals
          .filter(
            deal => getRiskBucket(getIndicatorScore(deal, indicator)) !== "low",
          )
          .toSorted((a, b) => (
            getIndicatorScore(b, indicator) - getIndicatorScore(a, indicator)
            || finiteNumber(b.value) - finiteNumber(a.value)
          ));
        return {
          count: indicatorDeals.length,
          deals: indicatorDeals,
          forecastValue: sumForecastValues(indicatorDeals),
          indicator,
          label: mapRiskName(indicator),
          rawValue: sumDealValues(indicatorDeals),
        };
      })
      .filter(row => row.count > 0)
      .toSorted((a, b) => b.rawValue - a.rawValue || b.forecastValue - a.forecastValue);
    const themeDeals = uniqueDeals(indicators.flatMap(indicator => indicator.deals));
    const forecastValue = sumForecastValues(themeDeals);

    return {
      count: themeDeals.length,
      deals: themeDeals,
      forecastValue,
      indicators,
      label: mapRiskGroupName(riskGroup),
      rawValue: sumDealValues(themeDeals),
      riskGroup,
      shareOfExposedDealValue: exposedOpenDealValue > 0
        ? sumDealValues(themeDeals) / exposedOpenDealValue
        : null,
    };
  }).filter(theme => theme.count > 0)
    .toSorted((a, b) => b.rawValue - a.rawValue || b.forecastValue - a.forecastValue);

  const timingDefinitions: StateOfSalesV2PipelineRiskAnalysis["timing"] = [
    { count: 0, deals: [], forecastValue: 0, key: "overdue", label: "Overdue", rawValue: 0 },
    { count: 0, deals: [], forecastValue: 0, key: "next_30", label: "Next 30 days", rawValue: 0 },
    { count: 0, deals: [], forecastValue: 0, key: "days_31_60", label: "31–60 days", rawValue: 0 },
    { count: 0, deals: [], forecastValue: 0, key: "later", label: "Later", rawValue: 0 },
    { count: 0, deals: [], forecastValue: 0, key: "not_specified", label: "Not specified", rawValue: 0 },
  ];
  const startOfEvaluationDay = new Date(evaluationDate);
  startOfEvaluationDay.setHours(0, 0, 0, 0);

  for (const deal of deals) {
    const expectedCloseDate = getExpectedCloseDate(deal);
    const dayDifference = expectedCloseDate
      ? Math.ceil((expectedCloseDate.getTime() - startOfEvaluationDay.getTime()) / 86_400_000)
      : null;
    const key: PipelineRiskTimingRow["key"] = dayDifference === null
      ? "not_specified"
      : dayDifference < 0
        ? "overdue"
        : dayDifference <= 30
          ? "next_30"
          : dayDifference <= 60
            ? "days_31_60"
            : "later";
    const row = timingDefinitions.find(item => item.key === key);
    if (!row) continue;
    row.count += 1;
    row.deals.push(deal);
    row.forecastValue += finiteNumber(deal.forecast_contribution);
    row.rawValue += finiteNumber(deal.value);
  }

  return {
    deals,
    exposedForecastShare: selectedPeriodForecast > 0
      ? exposedForecastValue / selectedPeriodForecast
      : null,
    exposedForecastValue,
    exposedOpenDealValue,
    selectedPeriodForecast,
    severity,
    themes,
    timing: timingDefinitions.filter(row => row.count > 0),
  };
}

export type PipelineHealthStageRow = {
  coveragePercentage: number;
  expectedValue: number;
  key: string;
  label: string;
  probabilityEnd: number;
  probabilityStart: number;
  requiredValue: number;
};

export function buildStateOfSalesV2PipelineHealthStages(
  chartData: Array<{
    close_probability?: number;
    coverage_percentage?: number;
    proportional_target?: number;
    value?: number;
  }>,
): PipelineHealthStageRow[] {
  return chartData
    .filter(row => finiteNumber(row.close_probability) >= 0.2
      && finiteNumber(row.close_probability) <= 1)
    .map((row) => {
      const probabilityEnd = Math.round(finiteNumber(row.close_probability) * 100);
      const probabilityStart = probabilityEnd - 20;
      return {
        coveragePercentage: finiteNumber(row.coverage_percentage),
        expectedValue: finiteNumber(row.value),
        key: String(row.close_probability),
        label: `${probabilityStart}–${probabilityEnd}% probability`,
        probabilityEnd,
        probabilityStart,
        requiredValue: finiteNumber(row.proportional_target),
      };
    });
}
